import {
  sniffImageBytes,
  isAllowedImageKind,
  rejectCopy,
  CLIENT_MAX_BYTES,
  TARGET_MAX_BYTES,
  MAX_EDGE,
} from './imageSniff';

/** Appilix / low-RAM WebViews die decoding full camera frames — prefer a smaller edge. */
const MOBILE_MAX_EDGE = 1920;

/** Hard ceiling for the multipart body we send (must match backend multer limit). */
const UPLOAD_MAX_BYTES = CLIENT_MAX_BYTES;

function prefersLowMemoryDecode() {
  if (typeof window === 'undefined') return false;
  if (window.appilix) return true;
  const ua = navigator.userAgent || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

async function readHead(file) {
  const slice = file.slice(0, 32);
  const buffer = await slice.arrayBuffer();
  return new Uint8Array(buffer);
}

function tryCanvasBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
}

async function downscale(bitmap, maxEdge, maxBytes) {
  let edge = maxEdge;
  let quality = 0.82;
  let lastBlob = null;

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false });
    ctx.drawImage(bitmap, 0, 0, width, height);

    let blob = await tryCanvasBlob(canvas, 'image/webp', quality);
    if (!blob) {
      blob = await tryCanvasBlob(canvas, 'image/jpeg', Math.max(quality, 0.7));
    }
    canvas.width = 0;
    canvas.height = 0;

    if (!blob) {
      break;
    }
    lastBlob = blob;
    if (blob.size <= maxBytes) {
      return blob;
    }
    quality = Math.max(0.45, quality - 0.12);
    edge = Math.max(1024, Math.round(edge * 0.75));
  }

  return lastBlob;
}

async function decodeViaImageElement(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('IMAGE_DECODE'));
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    if (!canvas.width || !canvas.height) {
      throw new Error('IMAGE_DECODE');
    }
    const ctx = canvas.getContext('2d', { alpha: false });
    ctx.drawImage(img, 0, 0);
    const bitmap = await createImageBitmap(canvas);
    canvas.width = 0;
    canvas.height = 0;
    return bitmap;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function decodeBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      try {
        return await createImageBitmap(file);
      } catch {
        // Fall through to <img> decode (helps some Android / HEIC-in-Safari paths).
      }
    }
  }
  return decodeViaImageElement(file);
}

function originalAsPrepared(file, sniffedKind) {
  const typeByKind = {
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
  };
  return {
    blob: file,
    contentType: file.type || typeByKind[sniffedKind] || 'image/jpeg',
    capturedAt: file.lastModified ? new Date(file.lastModified).toISOString() : null,
    originalName: file.name || `photo-${Date.now()}.jpg`,
    originalBytes: file.size,
    processedBytes: file.size,
  };
}

export async function prepareMemoryImage(file) {
  if (file.size > CLIENT_MAX_BYTES) {
    const error = new Error(rejectCopy('TOO_LARGE', file.size));
    error.reason = 'TOO_LARGE';
    throw error;
  }

  const head = await readHead(file);
  const sniffed = sniffImageBytes(head, file.name);
  if (!isAllowedImageKind(sniffed.kind)) {
    const error = new Error(rejectCopy(sniffed.reason, file.size));
    error.reason = sniffed.reason;
    throw error;
  }

  const maxEdge = prefersLowMemoryDecode() ? MOBILE_MAX_EDGE : MAX_EDGE;

  let bitmap;
  try {
    bitmap = await decodeBitmap(file);
  } catch (err) {
    // Phone JPEG/PNG/WebP: if the browser cannot re-encode, send the original —
    // the server accepts up to 30 MB and sharp re-encodes for storage.
    if (['jpeg', 'png', 'webp'].includes(sniffed.kind) && file.size <= UPLOAD_MAX_BYTES) {
      return originalAsPrepared(file, sniffed.kind);
    }
    const error = new Error(
      sniffed.kind === 'heic'
        ? 'This HEIC could not be converted here. Export a JPEG and try again.'
        : rejectCopy('UNSUPPORTED_TYPE', file.size)
    );
    error.reason = sniffed.kind === 'heic' ? 'HEIC_UNSUPPORTED' : 'UNSUPPORTED_TYPE';
    throw error;
  }

  try {
    const blob = await downscale(bitmap, maxEdge, TARGET_MAX_BYTES);
    if (!blob) {
      if (['jpeg', 'png', 'webp'].includes(sniffed.kind) && file.size <= UPLOAD_MAX_BYTES) {
        return originalAsPrepared(file, sniffed.kind);
      }
      const error = new Error(rejectCopy('UNSUPPORTED_TYPE', file.size));
      error.reason = 'UNSUPPORTED_TYPE';
      throw error;
    }

    if (blob.size > UPLOAD_MAX_BYTES) {
      const error = new Error(rejectCopy('TOO_LARGE', file.size));
      error.reason = 'TOO_LARGE';
      throw error;
    }

    return {
      blob,
      contentType: blob.type || 'image/webp',
      capturedAt: file.lastModified ? new Date(file.lastModified).toISOString() : null,
      originalName: file.name || `photo-${Date.now()}.jpg`,
      originalBytes: file.size,
      processedBytes: blob.size,
    };
  } finally {
    if (bitmap && bitmap.close) bitmap.close();
  }
}
